export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export async function fetchApi<T>(
  url: string,
  init?: RequestInit,
): Promise<T | null> {
  const res = await fetch(url, init);
  if (res.status === 401) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      body,
      (body as { error?: string; details?: string } | null)?.details ||
        (body as { error?: string } | null)?.error ||
        `GET ${url} failed: ${res.status}`,
    );
  }
  const payload = await res.json().catch(() => null);
  return ((payload as { data?: T } | null)?.data ?? null) as T | null;
}

export type MutateMethod = "POST" | "PUT" | "PATCH" | "DELETE";

export async function mutateApi<T>(
  method: MutateMethod,
  url: string,
  body?: unknown,
  init?: RequestInit,
): Promise<T> {
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;
  const hasBody = body != null;

  const headers: Record<string, string> = {};
  if (hasBody && !isFormData) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    ...(hasBody ? { body: isFormData ? (body as FormData) : JSON.stringify(body) } : {}),
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
    ...init,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      errBody,
      (errBody as { error?: string; details?: string } | null)?.details ||
        (errBody as { error?: string } | null)?.error ||
        `${method} ${url} failed: ${res.status}`,
    );
  }

  if (res.status === 204) return null as T;
  const json = await res.json().catch(() => ({}));

  const inlineError = (json as { error?: unknown } | null)?.error;
  if (inlineError) {
    throw new ApiError(
      res.status,
      json,
      typeof inlineError === "string"
        ? inlineError
        : `${method} ${url} returned inline error`,
    );
  }

  return ((json as { data?: T } | null)?.data ?? (json as T)) as T;
}
